import { dayNo, fmt, reward } from '../campaign';

const CheckPath = () => (
  <svg viewBox="0 0 24 24"><path d="M4 12.5l5 5L20 6.5" /></svg>
);

export function StreakTiles({ S, flipIndex, interactive, onInvest, targetId }) {
  const missAt = S.missed > 0 ? S.done : -1; // where the miss landed
  const tiles = [];
  const labels = [];
  for (let i = 0; i < S.window; i++) {
    const isDone = i < S.done;
    const isMiss = (S.missed > 0 && i === missAt) || (S.broken && i === S.done + 1 && S.missed > 1);
    const isToday = !S.broken && S.claim === 'none' && S.joined && !S.todayDone && i === dayNo(S) - 1;
    const isBonus = i === 7;
    let cls = 'gfg-tile';
    if (isDone) cls += ' gfg-done';
    else if (isMiss) cls += ' gfg-miss';
    else if (isToday) cls += ' gfg-today';
    else if (isBonus) cls += ' gfg-bonus';
    if (flipIndex === i) cls += ' gfg-flip';
    /* only today's tile does anything on tap — it jumps to the amount field */
    const onClick = interactive && isToday ? onInvest : undefined;
    tiles.push(
      interactive ? (
        <button key={i} className={cls} onClick={onClick}><CheckPath /></button>
      ) : (
        <div key={i} className={cls} id={targetId && i === S.done ? targetId : undefined}><CheckPath /></div>
      )
    );
    labels.push(
      <div key={i} className={'gfg-daylbl' + (i <= dayNo(S) - 1 ? ' gfg-on' : '')}>Day {i + 1}</div>
    );
  }
  return { tiles, labels };
}

/* tiles rendered straight from the API `card.days` array */
function ApiTiles({ days, flipIndex, onInvest }) {
  const tiles = [];
  const labels = [];
  days.forEach((d, i) => {
    let cls = 'gfg-tile';
    if (d.done) cls += ' gfg-done';
    else if (d.skipped) cls += ' gfg-miss';
    else if (d.isToday) cls += ' gfg-today';
    if (flipIndex === i) cls += ' gfg-flip';
    /* only today's tile does anything on tap — it jumps to the amount field */
    const onClick = d.isToday && !d.done ? onInvest : undefined;
    tiles.push(<button key={i} className={cls} onClick={onClick}><CheckPath /></button>);
    labels.push(
      <div key={i} className={'gfg-daylbl' + (d.done || d.skipped || d.isToday ? ' gfg-on' : '')}>Day {d.day}</div>
    );
  });
  return { tiles, labels };
}

export default function StreakSection({ S, copy, card, streakStatus, flipIndex, onCta, onOpenEarnings, onInvest }) {
  const { tiles, labels } = card && card.days && card.days.length
    ? ApiTiles({ days: card.days, flipIndex, onInvest })
    : StreakTiles({ S, flipIndex, interactive: true, onInvest });

  let earned;
  if (card && card.footerText) {
    earned = card.footerText;
  } else if (S.claim === 'credited' || S.claim === 'claimed') {
    earned = 'You earned <b>₹' + fmt(reward(S)) + '</b><br>on ₹' + fmt(S.invested) + ' of gold.';
  } else if (S.broken) {
    earned = 'You bought <b>₹' + fmt(S.invested) + '</b><br>worth of gold.';
  } else {
    earned = 'You have earned <b>₹' + fmt(S.invested) + '</b><br>worth of gold so far.';
  }

  const viewLabel = (card && card.viewEarnings && card.viewEarnings.text)
    || (S.claim === 'none' ? 'View earnings' : 'View reward');
  const cta = card && card.cta
    ? { label: card.cta.text, act: card.cta.action }
    : { label: copy.cta.label, act: copy.cta.act };
  let ctaCls = 'gfg-cta';
  if (copy.cta.cls) ctaCls += ' gfg-' + copy.cta.cls;
  if (card && card.cta && card.cta.enabled === false) ctaCls += ' gfg-dead';

  /* API card fields override the static copy when present */
  const sectionTitle = (card && card.title) || copy.title;

  /* alert: card.note (with highlight bolded) overrides static copy.alert */
  let alertBlock = null;
  if (card && card.note) {
    const alertHtml = card.noteHighlight
      ? card.note.replace(card.noteHighlight, '<b>' + card.noteHighlight + '</b>')
      : card.note;
    const alertCls = card.broken ? 'red' : card.urgent ? 'red' : 'amber';
    alertBlock = { cls: alertCls, html: alertHtml };
  } else if (copy.alert) {
    alertBlock = copy.alert;
  }

  /* note paragraph: card.subText overrides static copy.note */
  const noteHtml = (card && card.subText) || copy.note;

  /* badge class: use API broken/urgent flags when available */
  let badgeCls = copy.badge.cls ? ' gfg-' + copy.badge.cls : '';
  if (card && card.broken) badgeCls = ' gfg-bad';

  return (
    <section className="gfg-streak">
      <h2 className="gfg-sec-title gfg-rv">{sectionTitle}</h2>
      {(streakStatus === 'ACTIVE' || streakStatus === 'NOT_JOINED') && (
        <p className="gfg-sec-sub gfg-rv">Complete 7 daily Gold SIP to earn 2% in rewards.</p>
      )}

      <div className="gfg-card gfg-rv">
        {card && card.badge ? (
          <div className={'gfg-badge' + badgeCls}>{card.badge}</div>
        ) : (
          <div
            className={'gfg-badge' + (copy.badge.cls ? ' gfg-' + copy.badge.cls : '')}
            dangerouslySetInnerHTML={{ __html: copy.badge.html }}
          />
        )}
        <div className="gfg-tiles">{tiles}</div>
        <div className="gfg-daylbls">{labels}</div>
        <div className="gfg-cardfoot">
          <div className="gfg-earned" dangerouslySetInnerHTML={{ __html: earned }} />
          <button className="gfg-viewlink" onClick={() => (card && card.viewEarnings ? onCta(card.viewEarnings.action) : onOpenEarnings())}>
            {viewLabel}
          </button>
        </div>
      </div>

      {/* {alertBlock && (
        <div className={'gfg-alert gfg-' + (alertBlock.cls || 'amber')}>
          {alertBlock.ic && <span className="gfg-ic">{alertBlock.ic}</span>}
          <span dangerouslySetInnerHTML={{ __html: alertBlock.html }} />
        </div>
      )} */}
      {/* <p className="gfg-note gfg-rv" dangerouslySetInnerHTML={{ __html: noteHtml }} /> */}
      <div className="gfg-cta-wrap gfg-rv">
        <button className={ctaCls} onClick={() => onCta(cta.act)}>
          <span>{cta.label}</span>
          <svg viewBox="0 0 24 24"><path d="M4 12h15M13 6l6 6-6 6" /></svg>
        </button>
      </div>
    </section>
  );
}
